/**
 * Per-document suggestion list (R1 W2): "4 suggestions waiting · Methodology,
 * Team and 2 more". Each entry opens its section with the review strip. One
 * pending suggestion per section stays the rule, so an entry is a section.
 *
 * Presentational: the panel (`PanelPendingSuggestions`) and the document
 * overview (`ArtifactDetailPage`) feed it and decide what opening means.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SUGGESTION_OPEN, SUGGESTION_SHOWING } from '@/lib/ai-edit-copy'
import { suggestionsSummary, type PendingSuggestionItem } from '@/lib/ai-edit-view'

// ===================
// Presentational list
// ===================

interface PendingSuggestionsListProps {
  items: ReadonlyArray<PendingSuggestionItem>
  /** The suggestion the review strip is showing now, if any. */
  activeProposalId: string | null
  onOpen: (item: PendingSuggestionItem) => void
  className?: string
}

export function PendingSuggestionsList({ items, activeProposalId, onOpen, className }: PendingSuggestionsListProps) {
  const [isOpen, setIsOpen] = useState(false)
  // Nothing to point at when the only suggestion is the one on screen.
  if (items.length === 0 || items.every((item) => item.proposalId === activeProposalId)) return null

  return (
    <div className={cn('rounded-lg bg-muted/40', className)} data-testid="pending-suggestions-list">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-meta"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-ai-text" />
        <span className="min-w-0 flex-1 truncate">{suggestionsSummary(items)}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-subtle transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <ul className="divide-y divide-border px-3 pb-1">
          {items.map((item) => (
            <li key={item.proposalId} className="flex items-center justify-between gap-2 py-1.5 text-sm">
              <span className="min-w-0 truncate">{item.heading}</span>
              {item.proposalId === activeProposalId ? (
                <span className="text-meta shrink-0">{SUGGESTION_SHOWING}</span>
              ) : (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-0.5 text-xs text-[hsl(var(--link))] hover:underline"
                  aria-label={`${SUGGESTION_OPEN} ${item.heading}`}
                  onClick={() => onOpen(item)}
                >
                  {SUGGESTION_OPEN}
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
